/**
 * Google Analytics 4 (GA4) 設定ユーティリティ - 拡張版
 */

// Google Analytics測定ID
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

// 開発環境でのトラッキング無効化フラグ
const DISABLE_IN_DEV = process.env.REACT_APP_GA_DISABLE_IN_DEV === 'true';

/**
 * URLからクエリパラメータを取得
 * @param {string} url - 解析対象URL
 * @returns {Object} クエリパラメータオブジェクト
 */
const getQueryParams = (url = window.location.href) => {
  const urlObj = new URL(url);
  const params = {};
  urlObj.searchParams.forEach((value, key) => {
    params[key] = value;
  });
  return params;
};

/**
 * Google Analytics初期化
 */
export const initGA = () => {
  // 開発環境でトラッキング無効化が設定されている場合はスキップ
  if (DISABLE_IN_DEV && process.env.NODE_ENV === 'development') {
    console.log('GA4: 開発環境のためトラッキングを無効化');
    return;
  }

  // 測定IDが設定されていない場合はエラーログ
  if (!GA_MEASUREMENT_ID) {
    console.error('GA4: 測定ID (REACT_APP_GA_MEASUREMENT_ID) が設定されていません');
    return;
  }

  // GTAGスクリプトの動的読み込み
  const script1 = document.createElement('script');
  script1.async = true;
  script1.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script1);

  // GTAGの初期化スクリプト
  const script2 = document.createElement('script');
  script2.innerHTML = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', '${GA_MEASUREMENT_ID}', {
      page_title: document.title,
      page_location: window.location.href,
      custom_map: {
        'custom_parameter_1': 'language',
        'custom_parameter_2': 'page_section'
      }
    });
  `;
  document.head.appendChild(script2);

  console.log('GA4: 初期化完了');
};

/**
 * ページビューイベント送信（言語パラメータ対応）
 * @param {string} path - ページパス
 * @param {string} title - ページタイトル
 */
export const trackPageView = (path, title = '') => {
  if (!GA_MEASUREMENT_ID || (DISABLE_IN_DEV && process.env.NODE_ENV === 'development')) {
    return;
  }

  if (typeof window.gtag === 'function') {
    // クエリパラメータを取得
    const queryParams = getQueryParams();
    const language = queryParams.lang || 'ja'; // デフォルトは日本語
    
    // ページビューイベント
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_path: path,
      page_title: title || document.title,
      page_location: window.location.href,
      language: language,
      custom_parameter_1: language
    });

    // カスタムイベント（言語付きページビュー）
    window.gtag('event', 'page_view_with_lang', {
      event_category: 'navigation',
      event_label: `${path}?lang=${language}`,
      language: language,
      page_path: path,
      custom_parameter_1: language
    });

    console.log(`GA4: ページビュー送信 - ${path} (lang: ${language})`);
  }
};

/**
 * カスタムイベント送信
 * @param {string} action - イベントアクション
 * @param {string} category - イベントカテゴリ
 * @param {string} label - イベントラベル
 * @param {number} value - イベントバリュー
 * @param {Object} customParams - カスタムパラメータ
 */
export const trackEvent = (action, category = 'engagement', label = '', value = 0, customParams = {}) => {
  if (!GA_MEASUREMENT_ID || (DISABLE_IN_DEV && process.env.NODE_ENV === 'development')) {
    return;
  }

  if (typeof window.gtag === 'function') {
    const queryParams = getQueryParams();
    const language = queryParams.lang || 'ja';
    
    const eventParams = {
      event_category: category,
      event_label: label,
      value: value,
      language: language,
      custom_parameter_1: language,
      ...customParams
    };

    window.gtag('event', action, eventParams);
    console.log(`GA4: イベント送信 - ${action}`, eventParams);
  }
};

/**
 * スライダー操作追跡
 * @param {string} action - 操作タイプ（next, prev, thumbnail）
 * @param {number} slideIndex - スライドインデックス
 */
export const trackSliderInteraction = (action, slideIndex) => {
  const queryParams = getQueryParams();
  const language = queryParams.lang || 'ja';
  
  trackEvent('slider_interaction', 'engagement', `${action}_slide_${slideIndex}`, 1, {
    slider_action: action,
    slide_index: slideIndex,
    language: language
  });
};

// デフォルトエクスポート用オブジェクト（ESLint警告修正）
const analytics = {
  initGA,
  trackPageView,
  trackEvent,
  trackSliderInteraction
};

export default analytics;